/**
 * useCreateServiceAccount Hook
 *
 * React Query mutation hook for creating a service account.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { serviceAccountService } from '../../../api/service-account.service';
import { SERVICE_ACCOUNTS_QUERY_KEY } from './useServiceAccounts';
import { getErrorMessage } from '../../../lib/utils/errors';
import type { ServiceAccountCreateDTO, ServiceAccountSanitized } from '../../../types/service-account.types';

interface UseCreateServiceAccountOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for creating a service account
 */
export const useCreateServiceAccount = (
  tenantId: string,
  options?: UseCreateServiceAccountOptions
): UseMutationResult<ServiceAccountSanitized, Error, ServiceAccountCreateDTO> => {
  const queryClient = useQueryClient();

  return useMutation<ServiceAccountSanitized, Error, ServiceAccountCreateDTO>({
    mutationFn: (data) => serviceAccountService.createServiceAccount(tenantId, data),
    onSuccess: () => {
      // Invalidate service accounts list to refresh the data
      queryClient.invalidateQueries({ queryKey: [SERVICE_ACCOUNTS_QUERY_KEY, tenantId] });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(getErrorMessage(error));
    },
  });
};
